import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IconCircleCheck,
  IconConfetti,
  IconClock,
  IconAlertTriangle,
  IconLock,
  IconLoader2,
} from "@tabler/icons-react";
import classes from "./welcomeRedesign.module.css";
import { Logo } from "../../../constants";

const COPY = {
  talent: {
    title: "Registration Received",
    intro: "You're much closer to completing your registration. Here's what happens next.",
    steps: [
      {
        icon: IconClock,
        bg: "#eff2ff",
        color: "#1d4ed8",
        title: "Takes less than 10 minutes",
        body: "You'll be redirected to a simple onboarding page to add your details.",
      },
      {
        icon: IconAlertTriangle,
        bg: "#fef3c7",
        color: "#d97706",
        title: "Be honest and accurate",
        body: "Providing false information may get your account blacklisted.",
      },
      {
        icon: IconLock,
        bg: "#ecfdf5",
        color: "#059669",
        title: "Your data stays private",
        body: "Never shared with anyone else without your permission.",
      },
    ],
    destination: { pathname: "/talent-onboarding", search: "stage=1" },
  },
  client: {
    title: "Welcome on board",
    intro: "You are closer to completing your setup.",
    steps: [
      {
        icon: IconClock,
        bg: "#eff2ff",
        color: "#1d4ed8",
        title: "Takes less than 5 minutes",
        body: "You'll be redirected to a quick page to add your company details.",
      },
      {
        icon: IconAlertTriangle,
        bg: "#fef3c7",
        color: "#d97706",
        title: "Be honest and accurate",
        body: "Providing false information may get your account blacklisted.",
      },
      {
        icon: IconLock,
        bg: "#ecfdf5",
        color: "#059669",
        title: "Your data stays private",
        body: "Never shared with anyone else without your permission.",
      },
    ],
    destination: { pathname: "/client-onboarding", search: "stage=1" },
  },
};

const WelcomeRedesign = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const role = state === "client" ? "client" : state === "talent" ? "talent" : null;
  const [agreed, setAgreed] = useState(false);
  const [load, setLoad] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!role) window.location.href = "/";
  }, [role]);

  if (!role) return null;

  const copy = COPY[role];

  const getStarted = () => {
    if (!agreed) return;
    setLoad(true);
    setTimeout(() => navigate(copy.destination), 500);
  };

  return (
    <div className={classes.pageBg}>
      <div className={`${classes.brandBlob} ${classes.b1}`} />
      <div className={`${classes.brandBlob} ${classes.b2}`} />
      <div className={`${classes.brandBlob} ${classes.b3}`} />

      <div className={classes.pageWrap}>
        <div className={classes.centerCol}>
          <a className={`${classes.topLogo} ${classes.animIn}`} href="https://www.awasource.com">
            <Logo color="#ffffff" />
          </a>

          <div className={`${classes.card} ${classes.animIn}`}>
            <div className={classes.successBadge}>
              <IconCircleCheck size={38} />
            </div>

            <span className={classes.eyebrow}>
              <IconConfetti size={13} /> Account created
            </span>
            <h1 className={classes.cardTitle}>{copy.title}</h1>
            <p className={classes.cardIntro}>{copy.intro}</p>

            <div className={classes.stepList}>
              {copy.steps.map((step) => (
                <div className={classes.stepItem} key={step.title}>
                  <span className={classes.stepIcon} style={{ background: step.bg, color: step.color }}>
                    <step.icon size={18} />
                  </span>
                  <div>
                    <div className={classes.stepTextTitle}>{step.title}</div>
                    <div className={classes.stepTextBody}>{step.body}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className={classes.checkRow}>
              <input type="checkbox" id="agreeTerms" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
              <label htmlFor="agreeTerms">
                I have read and understood the{" "}
                <a href="/terms-of-service" target="_blank" rel="noreferrer">
                  Terms of use
                </a>{" "}
                and{" "}
                <a href="/privacy-policy" target="_blank" rel="noreferrer">
                  Privacy policy
                </a>
                .
              </label>
            </div>

            <button type="button" className={classes.btnPrimary} disabled={!agreed || load} onClick={getStarted}>
              {load ? (
                <>
                  <IconLoader2 size={16} className={classes.spinIcon} /> Taking you there…
                </>
              ) : (
                "Get Started"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeRedesign;
